import React, { useRef, useEffect, useState } from 'react';
import './App.scss';

import PageTraiteur from './components/pages/PageTraiteur';
import PageCatering from './components/pages/PageCatering';
import PageConcept from './components/pages/PageConcept';
import PageRestoBingo from './components/pages/PageRestoBingo';
import PageProducteurices from './components/pages/PageProducteurices';
import PageProjetsComestibles from './components/pages/PageProjetsComestibles';
import ArchiveProject from './components/pages/ArchiveProject';

import Home from './components/pages/Home';

import MenuTop from './components/MenuTop';
import LogoBackground from './components/LogoBackground';
import Loader from './components/Loader';
import MenuBottom from './components/MenuBottom';
import QuiSuisJe from './components/pages/QuiSuisJe';
import Infos from './components/pages/Infos';

import { motion } from "motion/react"

import client from './contentfulClientToken';

import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import LogoBackground02 from './components/LogoBackground02';
import MenuMobile from './components/MenuMobile';

// List of section routes (friendly URL slugs) and their corresponding DOM ids
const sectionRoutes = [
    { path: '/concept', id: 'concept' },
    { path: '/traiteur', id: 'traiteur' },
    { path: '/catering', id: 'catering' },
    { path: '/restobingo', id: 'restoBingo' },
    { path: '/projets-comestibles', id: 'projetsComestibles' },
    { path: '/producteurices', id: 'producteurices' },
    { path: '/qui-suis-je', id: 'quiSuisJe' },
    { path: '/infos', id: 'infosEtDevis' }
];

function scrollToSectionByPath(path) {
    const clean = (path || '/').replace(/\/+$/, '') || '/';
    const found = sectionRoutes.find(r => r.path === clean);
    if (found) {
        const el = document.getElementById(found.id) || document.querySelector(`[data-content-type="${found.id}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
}

function BackButton() {
    const navigate = useNavigate();
    const location = useLocation();

    if (location.pathname === '/') return null;

    return (
        <button
            onClick={() => navigate('/')}
            style={{
                position: 'fixed',
                top: '0',
                left: '0',
                zIndex: 1000,
                background: 'transparent',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '15px',
                color: 'grey',
            }}
        >
            retour
        </button>
    );
}

function App() {
    const [archive, setArchive] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [slideIn, setSlideIn] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);


    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const menus = [
            ...document.querySelectorAll('.menu-top'),
            ...document.querySelectorAll('.menu-mobile'),
        ];
        const sections = [...document.querySelectorAll('section')];

        // Apply smooth fade and ensure we override CSS animations on menu-top
        const apply = (el, isMenu) => {
            if (!(el instanceof HTMLElement)) return;
            // add transition if not present
            const prevTransition = el.style.transition;
            if (!prevTransition) el.style.transition = 'opacity 0.35s ease';

            if (hidden) {
                // override animation layer
                el.style.setProperty('opacity', '0', 'important');
                if (isMenu) {
                    el.style.setProperty('animation', 'none', 'important');
                    el.style.pointerEvents = 'none';
                }
            } else {
                el.style.setProperty('opacity', '1', 'important');
                if (isMenu) {
                    el.style.removeProperty('animation');
                    el.style.pointerEvents = '';
                }
            }
        };

        menus.forEach(el => apply(el, true));
        sections.forEach(el => apply(el, false));

        return () => {
            [...menus, ...sections].forEach(el => {
                if (!(el instanceof HTMLElement)) return;
                el.style.removeProperty('opacity');
                el.style.removeProperty('animation');
                el.style.removeProperty('pointer-events');
                // keep other transitions intact; don't forcibly remove transition
            });
        };
    }, [hidden]);


    // Attach emoji cursor follower after mount
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const cursorEl = document.querySelector('.emoji-cursor');
        if (!cursorEl) return;

        const onMove = (e) => {
            // Use viewport coordinates so position: fixed follows correctly while scrolling
            cursorEl.style.left = e.clientX + 'px';
            cursorEl.style.top = e.clientY + 'px';
            // Show emoji only when hovering a link
            const overLink = e.target && e.target.closest && e.target.closest('a');
            document.body.classList.toggle('show-emoji-cursor', !!overLink);
        };
        document.addEventListener('mousemove', onMove, { passive: true });
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.body.classList.remove('show-emoji-cursor');
        };
    }, []);


    useEffect(() => {
        client.getEntries({ content_type: 'archive' })
            .then(response => {
                console.log(response.items);
                setArchive(response.items);
            })
            .catch(error => {
                console.error(error);
            });

    }, []);

    const fadeInOptions = {
        initial: { opacity: 0, },
        whileInView: { opacity: 1, },
        transition: { duration: 0.5, ease: "easeOut" },
    };

    useEffect(() => {
        // Keep loader visible (behind content). Trigger slide after the 3 parts are on screen (~2.0s)
        const slideTimer = setTimeout(() => setSlideIn(true), 2000);

        // Show menu after slide completes (~2.5s after slide starts)
        const showMenuTimer = setTimeout(() => setMenuVisible(true), 4000);

        return () => {
            clearTimeout(slideTimer);
            clearTimeout(showMenuTimer);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const el = entry.target;
                    if (!(el instanceof HTMLElement)) return;
                    if (entry.isIntersecting) {
                        el.classList.add('in-view');
                    } else {
                        el.classList.remove('in-view');
                    }
                });
            },
            {
                root: null,
                // Narrow centered band so opacity hits 1 when the section crosses mid-screen
                rootMargin: '-50% 0px -50% 0px',
                threshold: 0,
            }
        );

        const observeAllSections = () => {
            document.querySelectorAll('section').forEach((sec) => io.observe(sec));
        };

        // Initial attach
        observeAllSections();

        const mo = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return; // not an element
                    const el = node;
                    if (el.matches && el.matches('section')) {
                        io.observe(el);
                    }
                    if (el.querySelectorAll) {
                        el.querySelectorAll('section').forEach((sec) => io.observe(sec));
                    }
                });
            });
        });

        mo.observe(document.body, { childList: true, subtree: true });

        return () => {
            io.disconnect();
            mo.disconnect();
        };
    }, []);


    // Listen for route changes and scroll to section
    useEffect(() => {
        const handleRoute = () => {
            scrollToSectionByPath(window.location.pathname);
        };
        window.addEventListener('popstate', handleRoute);
        // On mount, check initial path
        handleRoute();
        return () => window.removeEventListener('popstate', handleRoute);
    }, []);

    // Update URL when scrolling to a section (pushState)
    useEffect(() => {
        const sections = sectionRoutes.map(r => document.getElementById(r.id) || document.querySelector(`[data-content-type="${r.id}"]`)).filter(Boolean);
        if (!sections.length) return;
        let ticking = false;
        function onScroll() {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    // Using bounding rects to determine the closest section; no need for scrollY here.
                    let closest = { id: null, path: '/', diff: Infinity };
                    for (let i = 0; i < sectionRoutes.length; i++) {
                        const el = document.getElementById(sectionRoutes[i].id) || document.querySelector(`[data-content-type="${sectionRoutes[i].id}"]`);
                        if (!el) continue;
                        const rect = el.getBoundingClientRect();
                        const diff = Math.abs(rect.top);
                        if (diff < closest.diff) {
                            closest = { id: sectionRoutes[i].id, path: sectionRoutes[i].path, diff };
                        }
                    }
                    if (closest.path && window.location.pathname !== closest.path) {
                        window.history.replaceState({}, '', closest.path);
                    }
                    ticking = false;
                });
                ticking = true;
            }
        }
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Scroll-based background gradient from top (#cccccc) to bottom (#dfbfbf)
    useEffect(() => {
        if (typeof window === 'undefined') return;
        /* 
                const start = { r: 0xdf, g: 0xbf, b: 0xbf };
                const end = { r: 0xcc, g: 0xcc, b: 0xcc }; */

        const start = { r: 0xcc, g: 0xcc, b: 0xcc };
        const end = { r: 0xdf, g: 0xbf, b: 0xbf };

        const lerp = (a, b, t) => Math.round(a + (b - a) * t);
        const toHex = (n) => n.toString(16).padStart(2, '0');

        const update = () => {
            const doc = document.documentElement;
            const maxScroll = Math.max(1, doc.scrollHeight - window.innerHeight);
            const t = Math.min(1, Math.max(0, window.scrollY / maxScroll));
            const r = lerp(start.r, end.r, t);
            const g = lerp(start.g, end.g, t);
            const b = lerp(start.b, end.b, t);
            const color = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
            doc.style.backgroundColor = color;
            document.body.style.backgroundColor = color;
        };

        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);

        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    // Determine if current route is an archive page
    const isArchivePage = () => {
        if (typeof window === 'undefined') return false;
        const path = window.location.pathname.replace(/\/+$/, '');
        return archive.some((projet) => `/${projet.fields.url}` === path);
    };

    // Skip loader and slide for archive pages
    useEffect(() => {
        const applyArchiveFlags = () => {
            const path = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') : '';
            const onArchive = archive.some((projet) => `/${projet.fields.url}` === path);
            if (onArchive) {
                setSlideIn(true);
                setShowLoader(false);
            }
        };
        applyArchiveFlags();
        const onPop = () => applyArchiveFlags();
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
        // Re-evaluate when archive routes are fetched
    }, [archive]);

    return (
        <div className="App" >
            <Router>
                <BackButton />
                <div className={`app-slide-container ${(slideIn || isArchivePage()) ? 'is-visible' : ''}`}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/concept" element={<Home />} />
                        <Route path="/qui-suis-je" element={<Home />} />
                        <Route path="/traiteur" element={<Home />} />
                        <Route path="/catering" element={<Home />} />
                        <Route path="/restobingo" element={<Home />} />
                        <Route path="/projets-comestibles" element={<Home />} />
                        <Route path="/producteurices" element={<Home />} />

                        <Route path="/infos" element={<Home />} />


                        {archive.map((projet) => (
                            <Route
                                key={projet.sys.id}
                                path={`/${projet.fields.url}`}
                                element={<ArchiveProject data={projet} />}
                            />
                        ))}
                    </Routes>
                </div>
            </Router>
            <span className="emoji-cursor">🥕</span>
            {menuVisible && !isArchivePage() && <MenuTop />}
            {menuVisible && !isArchivePage() && <MenuMobile />}
            <Loader />
            {menuVisible && !isArchivePage() &&
                <div

                    onClick={() => setHidden(h => !h)}
                    className='toggle-button'
                    style={{



                    }}
                >
                    {hidden ? '🌺' : 'Labo lol'}
                </div>
            }
        </div>
    );
}

export default App;
